import React from 'react';
import { useSelector } from 'react-redux';
import AIDropzone from './AIDropzone';
import AIFileList from './AIFileList';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useFileUpload } from './hooks/useFileUpload';
import { useOverrideStorage } from './hooks/useOverrideStorage';
import { UploadedFile } from './types';
import { DataCollectionAiAgentOperationType } from '../../../store/Models/Surveys/SurveyDataModel';
import { ApplicationState } from '../../../store';

interface AIFileUploadSectionProps {
    requestId?: string;
    isProcessing: boolean;
    hasAIAnswers: boolean;
    uploadedFiles: UploadedFile[];
    onUploadedFilesChange: (files: UploadedFile[]) => void;
    onError?: (errorMessage: string) => void;
    localizer?: IStringLocalizer;
    registerPopoversCb?: (popoverIds: string[]) => void;
    operationType: DataCollectionAiAgentOperationType;
}

export const AIFileUploadSection: React.FC<AIFileUploadSectionProps> = ({
    requestId,
    isProcessing,
    hasAIAnswers,
    uploadedFiles,
    onUploadedFilesChange,
    onError,
    localizer,
    registerPopoversCb,
    operationType
}) => {
    // Get responseId from survey response state for localStorage key
    const responseId = useSelector((state: ApplicationState) =>
        state.surveyResponseState?.responseId
    );

    // Use localStorage hook for override preference persistence
    const { isDataOverriden, setIsDataOverriden } = useOverrideStorage(responseId);

    // Custom hooks for file management
    const { uploadFiles, removeFile, clearAllFiles, fileListRef } = useFileUpload({
        uploadedFiles,
        hasAIAnswers,
        onUploadedFilesChange,
        onError,
        localizer
    });

    return (
        <>
            {/* Dropzone Section - Hide during processing */}
            {!isProcessing && (
                <div className="ai-file-dropzone-container">
                    <div className="ai-file-dropzone">
                        <AIDropzone
                            onFilesSelected={uploadFiles}
                            onError={onError}
                            localizer={localizer}
                        />
                    </div>
                </div>
            )}

            {/* File List Section - Hide during processing */}
            {!isProcessing && (
                <div ref={fileListRef}>
                    <AIFileList
                        requestId={requestId}
                        uploadedFiles={uploadedFiles}
                        isProcessing={isProcessing}
                        overrideExistingAnswers={isDataOverriden}
                        onOverrideChange={setIsDataOverriden}
                        onRemoveFile={removeFile}
                        onClearAllFiles={clearAllFiles}
                        onError={onError}
                        localizer={localizer}
                        registerPopoversCb={registerPopoversCb}
                        operationType={operationType}
                    />
                </div>
            )}
        </>
    );
};

export default AIFileUploadSection;
